import { useEffect, useRef, useState, useCallback } from 'react';

const MODEL = 'gpt-4o-realtime-preview-2024-12-17';
const PUBLIC_API_BASE_URL = 'https://2447a96fef21.ngrok-free.app/public';
const OPENAI_API_BASE_URL = 'https://api.openai.com/v1';

export type ChatItem = {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  text?: string;
  data?: unknown;
};

type CallBuf = { args: string };

export interface UseOpenAIRealtimeReturn {
  // Connection state
  connecting: boolean;
  connected: boolean;
  error: string | null;
  
  // Chat data
  chat: ChatItem[];
  
  // Actions
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  sendText: (text: string) => void;
}

export function useOpenAIRealtime(): UseOpenAIRealtimeReturn {
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chat, setChat] = useState<ChatItem[]>([]);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const callBuf = useRef<Map<string, CallBuf>>(new Map());
  const assistantLiveMap = useRef<Map<string, string>>(new Map());

  const pushChat = useCallback((item: Omit<ChatItem, 'id'>) => {
    const id = Math.random().toString(36).slice(2);
    setChat((prev) => [...prev, { id, ...item }]);
    return id;
  }, []);

  const patchChat = useCallback((id: string, patch: Partial<ChatItem>) => {
    setChat((prev) => prev.map(x => (x.id === id ? { ...x, ...patch } : x)));
  }, []);

  // Initialize audio element
  useEffect(() => {
    const audio = document.createElement('audio');
    audio.autoplay = true;
    document.body.appendChild(audio);
    audioRef.current = audio;
    return () => audio.remove();
  }, []);

  const connect = useCallback(async () => {
    setError(null);
    setConnecting(true);
    try {
      // 1) Get ephemeral key from our public API
      const tokenResp = await fetch(`${PUBLIC_API_BASE_URL}/open-ai/realtime/session`, {
        method: 'GET',
        headers: { // TODO: remove customer headers after testing with ngrok
          'ngrok-skip-browser-warning': 'true',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      if (!tokenResp.ok) throw new Error(await tokenResp.text());
      const tokenData = await tokenResp.json();
      const EPHEMERAL_KEY = tokenData?.client_secret?.value;
      if (!EPHEMERAL_KEY) throw new Error('No ephemeral key');

      // 2) Create PeerConnection
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      // 3) Handle audio from model
      pc.ontrack = (e) => {
        if (audioRef.current && e.streams?.[0]) {
          audioRef.current.srcObject = e.streams[0];
        }
      };

      // 4) Create DataChannel
      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;

      dc.onopen = () => {
        console.log('DataChannel open');

        // Send session configuration
        dc.send(JSON.stringify({
          type: 'session.update',
          session: {
            instructions: `You are a voice assistant. Respond with voice briefly and friendly.
If the request implies an action in my system, CALL the send_to_n8n tool
and pass in the "text" field EXACTLY the original request text from the user in the same language (no translation/paraphrasing).
If possible, add "language" (e.g.: ru, en). When the function output contains JSON with "message",
say exactly that message and briefly summarize the result.`,
            input_audio_transcription: { model: 'whisper-1' },
            voice: 'verse',
            turn_detection: { type: 'server_vad', silence_duration_ms: 500 },
            tools: [{
              type: 'function',
              name: 'send_to_n8n',
              description: 'Send original user request text to my n8n system',
              parameters: {
                type: 'object',
                properties: {
                  text: { type: 'string', description: 'Original user request' },
                  language: { type: 'string', description: 'Request language (ru/en/…); optional' }
                },
                required: ['text']
              }
            }],
            tool_choice: 'auto'
          }
        }));
      };

      dc.onmessage = async (evt) => {
        try {
          const msg = JSON.parse(evt.data);

          // Log key events
          if (
            msg.type?.startsWith('response.function_call_arguments') ||
            msg.type?.startsWith('response.output_text') ||
            msg.type === 'response.created' ||
            msg.type === 'response.output_item.added' ||
            msg.type === 'response.output_item.done' ||
            msg.type === 'response.done' ||
            msg.type === 'rate_limits.updated'
          ) {
            console.log('RT event:', msg.type, msg);
          }

          // Handle streaming text from assistant (subtitles)
          if (msg.type === 'response.output_text.delta') {
            const rid = msg.response_id || msg.response?.id || 'default';
            let chatId = assistantLiveMap.current.get(rid);
            if (!chatId) {
              chatId = pushChat({ role: 'assistant', text: '' });
              assistantLiveMap.current.set(rid, chatId);
            }
            // Add text chunk
            const current = chat.find(c => c.id === chatId)?.text ?? '';
            patchChat(chatId, { text: current + (msg.delta || '') });
          }

          // Collect function arguments
          if (msg.type === 'response.function_call_arguments.delta') {
            const id = msg.call_id;
            const cur = callBuf.current.get(id) || { args: '' };
            cur.args += msg.delta || '';
            callBuf.current.set(id, cur);
          }

          // When arguments are ready, call n8n through our API
          if (msg.type === 'response.function_call_arguments.done') {
            const id = msg.call_id;
            const cur = callBuf.current.get(id);
            let parsed: Record<string, unknown> = {};
            try { 
              parsed = JSON.parse(cur?.args || '{}'); 
            } catch {
              // Failed to parse, use empty object
            }

            // Show system message about sending request
            const pendingId = pushChat({
              role: 'tool',
              text: 'Sending request to n8n…'
            });

            const r = await fetch(`${PUBLIC_API_BASE_URL}/n8n/trigger`, {
              method: 'POST',
              headers: {  // TODO: remove customer headers after testing with ngrok
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'ngrok-skip-browser-warning': 'true',
                'Cache-Control': 'no-cache'
              },
              body: JSON.stringify(parsed)
            });
            const bodyText = await r.text();
            console.log('n8n response status/body:', r.status, bodyText);

            // Try to parse JSON response from n8n
            let displayText = bodyText.trim();
            let details: unknown = undefined;
            try {
              const obj = JSON.parse(bodyText);
              details = obj;
              if (typeof obj?.message === 'string') {
                displayText = obj.message;
              } else {
                displayText = JSON.stringify(obj);
              }
            } catch {
              // Not JSON - leave as is
            }

            // Update tool message with nice text and details
            patchChat(pendingId, { text: displayText, data: details });

            // Send function output as string
            const outputString = typeof details !== 'undefined'
              ? JSON.stringify(details)
              : displayText;

            // Return function result to model
            dc.send(JSON.stringify({
              type: 'conversation.item.create',
              item: { type: 'function_call_output', call_id: id, output: outputString }
            }));
            // Ask model to complete response (will vocalize results)
            dc.send(JSON.stringify({ type: 'response.create' }));

            callBuf.current.delete(id);
          }

        } catch {
          // Binary chunks and other data may arrive - silently skip
        }
      };

      // 5) Get microphone
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      mic.getTracks().forEach((t) => pc.addTrack(t, mic));

      // 6) SDP offer/answer
      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);
      const sdpResp = await fetch(`${OPENAI_API_BASE_URL}/realtime?model=${MODEL}`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          'Authorization': `Bearer ${EPHEMERAL_KEY}`,
          'Content-Type': 'application/sdp',
          'OpenAI-Beta': 'realtime=v1'
        }
      });
      const answerSDP = await sdpResp.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSDP });

      setConnected(true);
    } catch (e: unknown) {
      console.error(e);
      const errorMessage = e instanceof Error ? e.message : 'connect error';
      setError(errorMessage);
    } finally {
      setConnecting(false);
    }
  }, [pushChat, patchChat, chat]);

  const disconnect = useCallback(async () => {
    dcRef.current?.close();
    pcRef.current?.getSenders().forEach((s) => s.track?.stop());
    pcRef.current?.close();
    dcRef.current = null;
    pcRef.current = null;
    setConnected(false);
  }, []);

  const sendText = useCallback((text: string) => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open') return;
    
    // Show user message
    pushChat({ role: 'user', text });
    
    // Send to conversation
    dc.send(JSON.stringify({
      type: 'conversation.item.create',
      item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] }
    }));
    dc.send(JSON.stringify({ type: 'response.create' }));
  }, [pushChat]);

  return {
    connecting,
    connected,
    error,
    chat,
    connect,
    disconnect,
    sendText
  };
}
